import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectGroupComponent } from '../list/project-group/project-group.component';
import { toProjectVms, sanitizeProjectDetails } from '../list/list-grouping.util';
import { ProjectService } from '../services/project.service';
import { AppService } from '../services/app.service';
import { SiteVisitConfirmationDialogComponent } from '../site-visit/site-visit-confirmation-dialog/site-visit-confirmation-dialog.component';
import { ModalService } from '../services/modal.service';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { DeclineSuccessComponent } from '../decline-success/decline-success.component';
import { AcceptedOfferedProposalDialogComponent } from '../accepted-offered-proposal-dialog/accepted-offered-proposal-dialog.component';
import { AtpSignAcceptedDialogComponent } from '../atp-sign-accepted-dialog/atp-sign-accepted-dialog.component';
import { SignedAtpSiteVisit } from '../model/signed-atp-site-visit';
import { BatchDeclineSummary } from '../model/batch-decline';

@Component({
  standalone: true,
  selector: 'info-active',
  imports: [
    CommonModule,
    ProjectGroupComponent,
    SiteVisitConfirmationDialogComponent,
    DeclineSuccessComponent,
    AcceptedOfferedProposalDialogComponent,
    AtpSignAcceptedDialogComponent,
  ],
  templateUrl: './active-page.component.html',
  styleUrl: './active-page.component.scss',
})
export class ActivePage implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  data: any = null;
  loading: boolean = true;
  siteVisitConfirmationActive = false;
  declineSuccessActive: boolean = false;
  declineBatch: BatchDeclineSummary | null = null;
  isOfferAcceptedActive: boolean = false;
  // Plurality of the accept that opened the confirmation, carried on the
  // ModalService emission. Nothing on an all-ACTIVE project can be accepted,
  // so this reads `false` in practice; it is bound rather than hard-coded
  // because `open` and `multiple` now arrive together and reading only `open`
  // is what keeps this dialog closed.
  isOfferAcceptedMultiple: boolean = false;
  isAtpSignAccepted: boolean = false;
  // Site visits the last ATP signature on this page left schedulable, relayed
  // up from the product line that signed. Cleared when the confirmation
  // closes, so a later signature that opened nothing cannot show a stale
  // offer. Held on the page, never in a service — a root-provided subject
  // would keep the previous result across navigations.
  signedAtpSiteVisits: SignedAtpSiteVisit[] = [];
  isFixedPrice: boolean = false;
  isAtp: boolean = false;
  atpModalHeaderTitle: string = 'ATP Signed & Project Active';
  atpModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';
  address: any = { streetAddress: '', cityStateZip: '' };

  constructor(
    private projectService: ProjectService,
    private modalService: ModalService,
    private utilityService: CommonUtilityServiceService,
    private appService: AppService
  ) {}

  ngOnInit() {
    this.fetchData();
    this.modalService.siteVisitConfirmationModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.siteVisitConfirmationActive = isOpen;
      });
    this.modalService.isDeclineModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((detail) => {
        // CON-148 #6 - outside the guard on purpose; see invitations-page for the full reasoning.
        // Guarded, this only ever wrote `true`, so the second decline never re-opened the dialog.
        this.declineSuccessActive = detail.isOpen;
        if (detail.isOpen) {
          this.declineBatch = detail.batch ?? null;
          this.isFixedPrice = detail.isFixedPrice;
          this.isAtp = detail.isAtp;
          if (detail.siteDetail.site) {
            this.address = this.utilityService.formatAddress(
              detail.siteDetail
            );
          }
        }
      });
    this.modalService.isOfferAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((offer) => {
        this.isOfferAcceptedActive = offer.open;
        this.isOfferAcceptedMultiple = offer.multiple;
      });
    this.modalService.isAtpSignAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.isAtpSignAccepted = isOpen;
      });
    this.appService.setRedirectUrl('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByProjectId(index: number, item: any): string {
    return item?.projectId ?? `${index}`;
  }

  toggleCloseDeclineSuccessModal() {
    this.modalService.toggleCloseDeclineSuccessModal();
  }

  toggleCloseOfferAcceptedModal() {
    this.modalService.toggleCloseOfferAcceptedModal();
  }

  toggleCloseAtpSignAcceptedModal() {
    this.signedAtpSiteVisits = [];
    this.modalService.toggleCloseAtpSignAcceptedModal();
  }

  fetchData() {
    this.projectService
      .fetchData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        const filtered = sanitizeProjectDetails(result)
          .map((parent: any) => ({
            ...parent,
            sites: parent.sites.filter(
              (child: any) =>
                child?.proposal?.status === 'ACTIVE' ||
                child?.proposal?.status === 'REVIEW_PENDING' ||
                child?.proposal?.status === 'REVISION_REQUESTED' ||
                child?.proposal?.status === 'REVISION_PENDING'
            ),
          }))
          .filter(
            (parent: any) => parent.sites.length > 0 && parent.projectId
          );
        // Group the flat project/site/proposal rows into the nested
        // Project → Site → Product view-model the CONNECT 2.0 cards render.
        this.data = toProjectVms(filtered);
        this.loading = false;
      });
  }

  toggleCloseSiteVisitConfirmationModal() {
    this.modalService.toggleCloseSiteVisitConfirmationModal();
  }
}
